import { ReactNode, useCallback, useEffect, useState } from "react";
import { getAppBuildInfo, isNative, onAppResume } from "../../lib/platform";
import {
  fetchAppConfig,
  readCachedConfig,
  shouldBlock,
} from "../../lib/updateGate";
import { UpdateRequiredScreen } from "./UpdateRequiredScreen";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

interface UpdateRequiredGateProps {
  children: ReactNode;
}

/**
 * Wraps the whole app and swaps it for UpdateRequiredScreen when this binary
 * is older than the API's declared minimum.
 *
 * RENDERS CHILDREN IMMEDIATELY and checks asynchronously. Blocking the first
 * paint on a network call would add real launch latency to every session in
 * order to catch a case that fires roughly never — and would make an API
 * outage look like a frozen splash screen. The wall appears a beat later on
 * the rare occasion it is warranted.
 *
 * The cached verdict is read first so a device that has already been told it
 * is too old stays blocked even offline, then the live fetch refreshes it.
 *
 * Re-checks on resume. The binary most likely to be stale is the one that has
 * been backgrounded for weeks, and it would otherwise never look again.
 */
export const UpdateRequiredGate = ({ children }: UpdateRequiredGateProps) => {
  const [blocked, setBlocked] = useState(false);

  const check = useCallback(async () => {
    const info = await getAppBuildInfo();
    if (info === null) return;

    const cached = await readCachedConfig();
    if (shouldBlock(cached, info.platform, info.build)) setBlocked(true);

    const fresh = await fetchAppConfig(API_URL);
    // Only ever flips ON from the live config. A fetch that fails returns null
    // and must not clear a block the cache already established — otherwise
    // killing the API becomes a way to bypass the gate.
    if (fresh !== null)
      setBlocked(shouldBlock(fresh, info.platform, info.build));
  }, []);

  useEffect(() => {
    if (!isNative()) return undefined;
    void check();
    return onAppResume(() => void check());
  }, [check]);

  if (blocked) return <UpdateRequiredScreen />;
  return <>{children}</>;
};
